import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Badge, BlockStack, Button, Icon, InlineStack, SkeletonBodyText, Text, Tooltip } from '@shopify/polaris';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon } from '@shopify/polaris-icons';
import './ProjectDropboxFolders.css';

/*
  The project's Dropbox folder in the DOCS card: every folder with the number of files below it
  (GET /api/docgen/dropbox/project-folders), so nobody has to click through Dropbox to find where the files are.
  Every folder row is a plain link to GET /api/docgen/dropbox/project-folder-link, which redirects to the folder's
  shared link (a link always opens, a tab opened by script and pointed at Dropbox later stayed on about:blank).
  "Open project folder" keeps the old behaviour (creates the standard structure when it is missing).
*/
const fileCountLabel = (count) => `${count} ${count === 1 ? 'file' : 'files'}`;

const folderLink = (projectNumber, path) => `/api/docgen/dropbox/project-folder-link?${new URLSearchParams({ projectNumber, path })}`;

async function readJson(response, fallbackMessage) {
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.msg || fallbackMessage);
  return data;
}

function FolderRows({ projectNumber, folders, depth, expanded, onToggle }) {
  return folders.map((folder) => {
    const canExpand = folder.fileCount > 0 && folder.folders.length > 0;
    const isExpanded = canExpand && expanded.has(folder.path);
    return (
      <Fragment key={folder.path}>
        <div className={`pdf-row${folder.fileCount ? '' : ' pdf-row-empty'}`} style={{ paddingLeft: 8 + depth * 24 }}>
          {canExpand ? (
            <button type="button" className="pdf-toggle" aria-expanded={isExpanded} aria-label={`${isExpanded ? 'Hide' : 'Show'} subfolders of ${folder.name}`} onClick={() => onToggle(folder.path)}>
              <Icon source={isExpanded ? ChevronDownIcon : ChevronRightIcon} tone="subdued" />
            </button>
          ) : (
            <span className="pdf-toggle-spacer" />
          )}
          <a className="pdf-open" href={folderLink(projectNumber, folder.path)} target="_blank" rel="noopener noreferrer" title={`Open ${folder.name} on Dropbox`}>
            <span>
              <Icon source={FolderIcon} tone="subdued" />
            </span>
            <span className="pdf-name">{folder.name}</span>
            <span className="pdf-count">
              {folder.fileCount ? (
                <Badge tone="success">{fileCountLabel(folder.fileCount)}</Badge>
              ) : (
                <Text as="span" variant="bodySm" tone="subdued">
                  Empty
                </Text>
              )}
            </span>
          </a>
        </div>
        {isExpanded ? <FolderRows projectNumber={projectNumber} folders={folder.folders} depth={depth + 1} expanded={expanded} onToggle={onToggle} /> : null}
      </Fragment>
    );
  });
}

export default function ProjectDropboxFolders({ projectNumber, disabled = false }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openError, setOpenError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [openingRoot, setOpeningRoot] = useState(false);
  const loadController = useRef(null);
  const refreshTimer = useRef(null);

  const load = useCallback(async () => {
    if (!projectNumber) return;
    loadController.current?.abort();
    const controller = new AbortController();
    loadController.current = controller;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/docgen/dropbox/project-folders?projectNumber=${encodeURIComponent(projectNumber)}`, { signal: controller.signal });
      setTree(await readJson(response, 'Could not read the Dropbox folder of this project.'));
    } catch (loadError) {
      if (controller.signal.aborted) return;
      setError(loadError.message || 'Could not read the Dropbox folder of this project.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [projectNumber]);

  useEffect(() => {
    setTree(null);
    setExpanded(new Set());
    load();
    return () => {
      loadController.current?.abort();
      clearTimeout(refreshTimer.current);
    };
  }, [load]);

  const toggle = useCallback((path) => {
    setExpanded((current) => {
      const next = new Set(current);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  }, []);

  // the project folder itself; the server also (re)creates the standard folder structure here
  const openProjectFolder = useCallback(async () => {
    setOpenError('');
    setOpeningRoot(true);
    const wasMissing = tree?.exists === false;
    try {
      const response = await fetch('/api/docgen/dropbox/shareableurl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectNumber })
      });
      const { url } = await readJson(response, 'Could not open the Dropbox folder of this project.');
      window.open(url, '_blank');
      if (wasMissing) {
        load();
        // the standard subfolders are created in the background, a few seconds after the link
        clearTimeout(refreshTimer.current);
        refreshTimer.current = setTimeout(load, 15000);
      }
    } catch (rootError) {
      setOpenError(rootError.message);
    } finally {
      setOpeningRoot(false);
    }
  }, [projectNumber, tree, load]);

  let content;
  if (!tree && loading) {
    content = <SkeletonBodyText lines={4} />;
  } else if (!tree && error) {
    content = (
      <Text as="p" tone="critical">
        {error}
      </Text>
    );
  } else if (tree && !tree.exists) {
    content = (
      <Text as="p" tone="subdued">
        This project has no Dropbox folder yet.
      </Text>
    );
  } else if (tree) {
    content = (
      <BlockStack gap="200">
        <div className="pdf-list">
          <FolderRows projectNumber={projectNumber} folders={tree.folders} depth={0} expanded={expanded} onToggle={toggle} />
        </div>
        {tree.looseFileCount ? (
          <Text as="p" variant="bodySm" tone="subdued">
            {fileCountLabel(tree.looseFileCount)} directly in the project folder.
          </Text>
        ) : null}
        {tree.truncated ? (
          <Text as="p" variant="bodySm" tone="subdued">
            Very large folder: the counts may be incomplete.
          </Text>
        ) : null}
      </BlockStack>
    );
  }

  return (
    <BlockStack gap="300">
      {content}
      {openError || (tree && error) ? (
        <Text as="p" variant="bodySm" tone="critical">
          {openError || error}
        </Text>
      ) : null}
      <InlineStack gap="200" blockAlign="center">
        <Tooltip content="If folder exists, opens it online. If folder does not exists folder gets created and is then opened.">
          <Button disabled={disabled} loading={openingRoot} onClick={openProjectFolder}>
            {tree && !tree.exists ? 'Create Dropbox folder' : 'Open project folder'}
          </Button>
        </Tooltip>
        {tree || error ? (
          <Button variant="plain" onClick={load} loading={loading && Boolean(tree)}>
            Refresh
          </Button>
        ) : null}
      </InlineStack>
    </BlockStack>
  );
}
